// contexts/UserContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
} from "react";
import { User, UserAppData, PersonRecord } from "../types/data";
import { storageService } from "../services/storage.service";
import { APP_CONFIG } from "../config/app.config";
import { logService } from "../services/log.service";
import SplashScreen from "../components/SplashScreen";
import { createPersonRecordFromUser } from "../utils/personRecord";

// --- Context type ---
interface UserContextType {
  // Backward-compatible fields (older pages expect these)
  user: User | null;
  updateUser: (nextUser: User) => void;

  appData: UserAppData | null;
  setAppData: (data: UserAppData) => void;
  
  // User info (from auth)
  getUser: () => User | null;

  // Person helpers
  getPerson: (id: string) => PersonRecord | undefined;
  getUserPerson: () => PersonRecord | undefined;
  getSurveyedPersons: () => PersonRecord[];
  addPerson: (person: PersonRecord) => void;
  updatePerson: (person: PersonRecord) => void;
  updateUserPerson: (updated: PersonRecord) => void;
  deletePerson: (id: string) => void;
  
  // Reload data from storage (useful after sync)
  reloadData: () => Promise<void>;
}

// --- Default fallback context (should not be used directly) ---
export const UserContext = createContext<UserContextType>(
  {} as UserContextType
);

// --- Context provider ---
export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [appData, setAppDataState] = useState<UserAppData | null>(null);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const createDefaultAppData = async (): Promise<UserAppData> => {
    const settings = await storageService.getSettings();
    const now = new Date().toISOString();
    return {
      app_version: APP_CONFIG.VERSION,
      lang: settings.language,
      mode: APP_CONFIG.DEFAULT_MODE,
      weight_unit: APP_CONFIG.DEFAULT_WEIGHT_UNIT,
      persons: [],
      updated_at: now,
      pending_sync: false,
    };
  };

  // Load user data on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const stored = await storageService.getUserData();
        const authData = await storageService.getAuthData();

        const baseData: UserAppData = stored ?? (await createDefaultAppData());

        // Ensure the signed-in user always has a corresponding PersonRecord
        // (User Mode logs DayRecords against a PersonRecord)
        const signedInUser = authData?.user ?? null;

        if (signedInUser) {
          const signedInUserAny = signedInUser as any;
          const userPersonId =
            (typeof signedInUserAny.uuid === "string" && signedInUserAny.uuid.length > 0)
              ? signedInUserAny.uuid
              : String(signedInUser.id);

          const surveyorId = userPersonId;
          const persons = Array.isArray((baseData as any).persons)
            ? (baseData as any).persons
            : [];
          const hasUserPerson = persons.some((p: any) => p?.id === userPersonId);

          if (!Array.isArray((baseData as any).persons)) {
            (baseData as any).persons = persons;
          }

          if (!hasUserPerson) {
            const now = new Date().toISOString();
            persons.unshift(
              createPersonRecordFromUser({
                user: signedInUser,
                surveyorId,
                nowIso: now,
              })
            );

            const updated = {
              ...baseData,
              persons,
              updated_at: now,
              pending_sync: true,
            };

            await storageService.setUserData(updated);
            setAppDataState(updated);
          } else {
            if (stored == null) {
              // First launch: persist default structure so the app can render.
              await storageService.setUserData(baseData);
            }
            setAppDataState(baseData);
          }
        } else {
          if (stored == null) {
            // First launch (no auth yet): persist default structure so UI can render.
            await storageService.setUserData(baseData);
          }
          setAppDataState(baseData);
        }

        setCurrentUser(signedInUser);
      } catch (error) {
        console.error('Failed to load user data:', error);
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, []);

  // Listen for data updates from sync service
  useEffect(() => {
    const handleDataUpdate = () => {
      reloadData();
    };
    
    window.addEventListener('userDataUpdated', handleDataUpdate);
    return () => {
      window.removeEventListener('userDataUpdated', handleDataUpdate);
    };
  }, []);

  // Keep currentUser in sync with auth changes (login/logout/PIN changes)
  useEffect(() => {
    const unsubscribe = storageService.subscribeAuthData((authData) => {
      setCurrentUser(authData?.user ?? null);
    });
    return unsubscribe;
  }, []);

  const setAppData = async (data: UserAppData) => {
    const startTime = Date.now();
    logService.info('UserContext', 'setAppData - START', {
      personCount: data.persons?.length || 0,
      dataSize: JSON.stringify(data).length,
    });
    
    try {
      // Write to storage FIRST, then update state
      await storageService.setUserData(data);
      logService.info('UserContext', 'setAppData - Storage write complete', {
        duration: Date.now() - startTime,
      });
      
      setAppDataState(data);
      logService.info('UserContext', 'setAppData - State update complete', {
        duration: Date.now() - startTime,
      });
    } catch (error) {
      logService.error('UserContext', 'setAppData - FAILED', {
        error: error instanceof Error ? error.message : String(error),
        duration: Date.now() - startTime,
      });
      throw error;
    }
    
    logService.info('UserContext', 'setAppData - COMPLETE', {
      duration: Date.now() - startTime,
    });
  };

  const updateUser = (nextUser: User) => {
    setCurrentUser(nextUser);
    // Best-effort persist to auth storage (async fire-and-forget)
    storageService
      .getAuthData()
      .then((authData) => {
        if (!authData) return;
        return storageService.setAuthData({
          ...authData,
          user: nextUser,
        });
      })
      .catch(console.error);
  };

  const getUser = () => {
    return currentUser;
  };

  // --- Person management ---
  const getPerson = (id: string): PersonRecord | undefined => {
    return appData?.persons?.find((p) => p.id === id);
  };
  
  const getUserPerson = (): PersonRecord | undefined => {
    if (!currentUser || !appData?.persons) return undefined;
    return appData.persons.find((p) => p.id === currentUser.id);
  };
  
  const getSurveyedPersons = (): PersonRecord[] => {
    if (!currentUser || !appData?.persons) return [];
    // Get the current user's surveyor ID (uuid or id)
    const currentSurveyorId = (currentUser as any).uuid || currentUser.id;
    // Filter to only return persons created by the current user
    return appData.persons.filter((p) => {
      // Exclude the current user's own PersonRecord
      if (p.id === currentUser.id) return false;
      // Only include persons where surveyor_id matches current user
      return p.surveyor_id === currentSurveyorId;
    });
  };

  const addPerson = async (person: PersonRecord) => {
    if (!appData) return;
    const deletedPersons = Array.isArray((appData as any).deleted_persons)
      ? (appData as any).deleted_persons
      : [];
    const nextDeletedPersons = deletedPersons.filter((d: any) => d?.id !== person.id);
    await setAppData({
      ...appData,
      persons: [...appData.persons, person],
      deleted_persons: nextDeletedPersons,
      updated_at: new Date().toISOString(),
      pending_sync: true,
    });
  };

  const updatePerson = async (person: PersonRecord) => {
    if (!appData) return;
    
    const startTime = Date.now();
    logService.info('UserContext', 'updatePerson - START', {
      personId: person.id,
      recordCount: person.records?.length || 0,
    });
    
    const touched: PersonRecord = {
      ...person,
      updated_at: new Date().toISOString(),
    };

    const newPersons = appData.persons.map((p) =>
      p.id === touched.id ? touched : p
    );
    
    await setAppData({
      ...appData,
      persons: newPersons,
      updated_at: new Date().toISOString(),
      pending_sync: true,
    });
    
    logService.info('UserContext', 'updatePerson - COMPLETE', {
      duration: Date.now() - startTime,
    });
  };

  const updateUserPerson = async (updated: PersonRecord) => {
    await updatePerson(updated);
  };

  const deletePerson = async (id: string) => {
    if (!appData) return;
    if (currentUser && id === currentUser.id) {
      console.warn("Cannot delete the current user profile");
      return;
    }
    
    // Find the person being deleted to store metadata
    const personToDelete = appData.persons.find((p) => p.id === id);
    const newPersons = appData.persons.filter((p) => p.id !== id);
    const deletedPersons = Array.isArray((appData as any).deleted_persons)
      ? (appData as any).deleted_persons
      : [];
    const alreadyLogged = deletedPersons.some((d: any) => d?.id === id);
    
    // Store name and surveyor_id with the deleted record
    const nextDeletedPersons = alreadyLogged
      ? deletedPersons
      : [
          ...deletedPersons,
          {
            id,
            deleted_at: new Date().toISOString(),
            name: personToDelete?.name,
            surveyor_id: personToDelete?.surveyor_id,
          },
        ];
    
    await setAppData({
      ...appData,
      persons: newPersons,
      deleted_persons: nextDeletedPersons,
      updated_at: new Date().toISOString(),
      pending_sync: true,
    });
  };

  const reloadData = async () => {
    try {
      const stored = await storageService.getUserData();
      if (stored) {
        setAppDataState(stored);
      }
    } catch (error) {
      console.error('Error reloading user data:', error);
    }
  };

  // Show a visible loading state while data is being loaded (prevents blank screen)
  if (loading || !appData) {
    return <SplashScreen onComplete={() => {}} />;
  }

  return (
    <UserContext.Provider
      value={{
        user: currentUser,
        updateUser,
        appData,
        setAppData,
        getUser,
        getPerson,
        getUserPerson,
        getSurveyedPersons,
        addPerson,
        updatePerson,
        updateUserPerson,
        deletePerson,
        reloadData,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

// --- Custom hook for convenience ---
export const useUserData = () => useContext(UserContext);
